import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { errorCode } from "@/lib/api-errors";
import type {
  ApplyAmountChangeRequest,
  ApplyAmountChangeResponse,
  BillingConfig,
  InstanceEmployee,
  InstanceSubscription,
} from "@/types/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OneOffInvoiceCard } from "@/components/instance-one-off-invoice-card";
import { SubscriptionActionsCard } from "@/components/billing-ops/subscription-actions-card";
import { BillingResetCard } from "@/components/billing-ops/billing-reset-card";

interface Props {
  name: string;
  /** VAT rate as a percentage (e.g. 19), from the billing-config form state. */
  vatRatePercent: number;
  /** ISO currency code from the billing-config form state (e.g. "EUR"). */
  currency: string;
  /** Saved subscription + config (not the unsaved form) — drive which actions show. */
  subscription: InstanceSubscription | null;
  config: BillingConfig | null;
  /** Billing-exempt (internal) account — a plain billing reset does not block it. */
  internal: boolean;
}

/**
 * Operator-only billing actions that hit Stripe directly (as opposed to the
 * "Billing config" card, which only writes the control-plane DB):
 *  - change the recurring subscription amount immediately with proration;
 *  - issue an ad-hoc one-off line-item invoice;
 *  - subscribe by invoice, cancel / resume, portal link, sync with Stripe;
 *  - billing reset (forget other-mode / ended Stripe refs).
 * Backend routes live under /api/admin/instances/:name/billing/*.
 */
export function InstanceBillingOperationsSection({
  name,
  vatRatePercent,
  currency,
  subscription,
  config,
  internal,
}: Props) {
  return (
    <>
      <BillingResetCard name={name} subscription={subscription} internal={internal} />
      <SubscriptionActionsCard name={name} subscription={subscription} config={config} />
      <ChangeAmountCard name={name} />
      <OneOffInvoiceCard name={name} vatRatePercent={vatRatePercent} currency={currency} />
    </>
  );
}

function ChangeAmountCard({ name }: { name: string }) {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const [newAmountMajor, setNewAmountMajor] = React.useState("");
  const [setupFeeMajor, setSetupFeeMajor] = React.useState("");

  // Employee count is a free nicety: the same query key as
  // InstanceEmployeesSection, so it is served from the react-query cache.
  const { data: empData } = useQuery({
    queryKey: ["instance", name, "employees"],
    queryFn: () =>
      api.get<{ employees: InstanceEmployee[] }>(`/api/admin/instances/${name}/employees`),
  });
  const employeeCount = empData
    ? empData.employees.filter((e) => e.syncStatus !== "archived").length
    : null;

  const applyMutation = useMutation({
    mutationFn: (body: ApplyAmountChangeRequest) =>
      api.post<ApplyAmountChangeResponse>(
        `/api/admin/instances/${name}/billing/apply-amount-change`,
        body
      ),
    onSuccess: (res) => {
      toast.success(t("operations.changeAmount.applied", { invoiceId: res.latestInvoiceId }));
      qc.invalidateQueries({ queryKey: ["instance", name] });
      qc.invalidateQueries({ queryKey: ["billing", "registry"] });
    },
    onError: (err: unknown) => {
      const code = errorCode(err);
      if (code === "NO_ACTIVE_SUBSCRIPTION") {
        toast.error(t("operations.changeAmount.noActiveSubscription"));
      } else if (code === "NO_BILLING_CONFIG") {
        toast.error(t("operations.changeAmount.noBillingConfig"));
      } else if (err instanceof ApiError && err.status === 503) {
        toast.error(t("stripeNotConfigured"));
      } else {
        toast.error(err instanceof Error ? err.message : t("operations.changeAmount.failed"));
      }
    },
  });

  function handleApply() {
    const amount = Number(newAmountMajor);
    if (!newAmountMajor.trim() || !Number.isFinite(amount) || amount < 0) {
      toast.error(t("operations.changeAmount.invalidAmount"));
      return;
    }
    // Fresh idemKey per attempt so a double-click cannot double-charge.
    const body: ApplyAmountChangeRequest = {
      newAmountCents: Math.round(amount * 100),
      idemKey: crypto.randomUUID(),
    };
    if (setupFeeMajor.trim()) {
      const fee = Number(setupFeeMajor);
      if (!Number.isFinite(fee) || fee < 0) {
        toast.error(t("operations.changeAmount.invalidSetupFee"));
        return;
      }
      if (fee > 0) body.setupFeeCents = Math.round(fee * 100);
    }
    applyMutation.mutate(body);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("operations.changeAmount.title")}</CardTitle>
        <CardDescription>{t("operations.changeAmount.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="bo-new-amount">{t("operations.changeAmount.newAmount")}</Label>
            <Input
              id="bo-new-amount"
              inputMode="decimal"
              value={newAmountMajor}
              onChange={(e) => setNewAmountMajor(e.target.value)}
              placeholder="49.00"
            />
            {employeeCount !== null ? (
              <p className="text-xs text-muted-foreground">
                {t("operations.changeAmount.employees", { count: employeeCount })}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bo-setup-fee">{t("operations.changeAmount.setupFee")}</Label>
            <Input
              id="bo-setup-fee"
              inputMode="decimal"
              value={setupFeeMajor}
              onChange={(e) => setSetupFeeMajor(e.target.value)}
              placeholder="0.00"
            />
          </div>
        </div>

        <p className="text-xs text-muted-foreground">{t("operations.changeAmount.hint")}</p>

        <div className="flex justify-end">
          <Button onClick={handleApply} disabled={applyMutation.isPending}>
            {applyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("operations.changeAmount.apply")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
